import { combineReducers, configureStore, createAction } from "@reduxjs/toolkit";
import { alationReducer } from "@/features/data-hub/model/alationSlice";
import { appearanceReducer } from "@/features/appearance/model/appearanceSlice";
import { appsReducer } from "@/features/app-studio/model/appsSlice";
import { integrationsReducer } from "@/features/actions/model/integrationsSlice";
import { publishedAppsReducer } from "@/features/published-apps/model/publishedAppsSlice";
import { tenantConfigurationReducer } from "@/features/app-config/model/tenantConfigurationSlice";
import { tenantFeaturesReducer } from "@/features/app-config/model/tenantFeaturesSlice";
import { chatHistoryReducer, chatReducer } from "@/features/chat";
import { connectionsReducer } from "@/features/data-hub/model/connectionsSlice";
import { dataQualityReducer } from "@/features/data-hub/model/dataQualitySlice";
import { domainAccessReducer } from "@/features/domain-access/model/domainAccessSlice";
import { domainDetailReducer } from "@/features/data-hub/model/domainDetailSlice";
import { domainDocumentEmbeddingsReducer } from "@/features/data-hub/model/domainDocumentEmbeddingsSlice";
import { domainDocumentsReducer } from "@/features/data-hub/model/domainDocumentsSlice";
import { domainLearningsReducer } from "@/features/data-hub/model/domainLearningsSlice";
import { domainsReducer } from "@/features/data-hub/model/domainsSlice";
import { homeReducer } from "@/features/home/model/homeSlice";
import { jobApprovalWidgetsReducer } from "@/features/data-hub/model/jobApprovalWidgetsSlice";
import { jobClassificationReducer } from "@/features/data-hub/model/jobClassificationSlice";
import { nuggetConfigsReducer } from "@/features/data-hub/model/nuggetConfigsSlice";
import { nuggetsReducer } from "@/features/nuggets/model/nuggetsSlice";
import { rolesReducer } from "@/features/roles/model/rolesSlice";
import { scheduledJobsReducer } from "@/features/data-hub/model/scheduledJobsSlice";
import { tenantsReducer } from "@/features/admin/tenants/model/tenantsSlice";
import { learningHistoryReducer } from "@/features/context-hub/model/learningHistorySlice";
import { userPreferencesReducer } from "@/features/context-hub/model/userPreferencesSlice";
import { usersReducer } from "@/features/users/model/usersSlice";
import { brandingReducer } from "@/features/settings/model/brandingSlice";
import { businessContextReducer } from "@/features/settings/model/businessContextSlice";
import { llmApiKeysReducer } from "@/features/settings/model/llmApiKeysSlice";
import { logsReducer } from "@/features/settings/model/logsSlice";
import { tokenUsageReducer } from "@/features/settings/model/tokenUsageSlice";
import { ssoConfigurationReducer } from "@/features/settings/model/ssoConfigurationSlice";
import { ssoRoleAssignmentRuleReducer } from "@/features/sso-role-assignment-rules/model/ssoRoleAssignmentRuleSlice";
import { apiErrorListenerMiddleware } from "@/shared/notifications/middleware/apiErrorListenerMiddleware";
import { notificationReducer } from "@/shared/notifications/model/notificationSlice";

export const resetApplicationState = createAction("app/resetApplicationState");

const appReducer = combineReducers({
  appearance: appearanceReducer,
  alation: alationReducer,
  apps: appsReducer,
  publishedApps: publishedAppsReducer,
  chat: chatReducer,
  chatHistory: chatHistoryReducer,
  connections: connectionsReducer,
  dataQuality: dataQualityReducer,
  domainAccess: domainAccessReducer,
  domainDetail: domainDetailReducer,
  jobApprovalWidgets: jobApprovalWidgetsReducer,
  jobClassification: jobClassificationReducer,
  domainDocuments: domainDocumentsReducer,
  domainLearnings: domainLearningsReducer,
  domainDocumentEmbeddings: domainDocumentEmbeddingsReducer,
  domains: domainsReducer,
  home: homeReducer,
  notifications: notificationReducer,
  tenantConfiguration: tenantConfigurationReducer,
  // Deliberately not preserved across `resetApplicationState`, unlike `tenantConfiguration`: it is
  // fetched behind auth, so the next sign-in refetches it anyway.
  tenantFeatures: tenantFeaturesReducer,
  ssoConfiguration: ssoConfigurationReducer,
  ssoRoleAssignmentRule: ssoRoleAssignmentRuleReducer,
  businessContext: businessContextReducer,
  branding: brandingReducer,
  llmApiKeys: llmApiKeysReducer,
  tokenUsage: tokenUsageReducer,
  logs: logsReducer,
  nuggets: nuggetsReducer,
  nuggetConfigs: nuggetConfigsReducer,
  scheduledJobs: scheduledJobsReducer,
  integrations: integrationsReducer,
  users: usersReducer,
  roles: rolesReducer,
  tenants: tenantsReducer,
  userPreferences: userPreferencesReducer,
  learningHistory: learningHistoryReducer,
});

const rootReducer: typeof appReducer = (state, action) => {
  if (resetApplicationState.match(action)) {
    return appReducer(
      {
        tenantConfiguration: state?.tenantConfiguration,
        appearance: state?.appearance,
      },
      action,
    );
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(apiErrorListenerMiddleware.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
